import AlternateEmailOutlinedIcon from '@mui/icons-material/AlternateEmailOutlined'
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded'
import CloseIcon from '@mui/icons-material/Close'
import ForumOutlinedIcon from '@mui/icons-material/ForumOutlined'
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded'
import WhatsAppIcon from '@mui/icons-material/WhatsApp'
import {
  Box,
  CircularProgress,
  Drawer,
  IconButton,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material'
import dayjs from 'dayjs'
import { useCallback, useEffect, useState } from 'react'
import { loadQuickActionDeliverySummary } from './patientQuickActionsService'

// Colors for the delivery status; keyed on the canonical Twilio status.
const STATUS_COLORS = {
  read: '#059669',
  delivered: '#059669',
  sent: '#d97706',
  queued: '#d97706',
  accepted: '#d97706',
  sending: '#d97706',
  undelivered: '#dc2626',
  failed: '#dc2626',
}
const STATUS_BG = {
  read: 'rgba(5, 150, 105, 0.1)',
  delivered: 'rgba(5, 150, 105, 0.1)',
  sent: 'rgba(217, 119, 6, 0.1)',
  queued: 'rgba(217, 119, 6, 0.1)',
  accepted: 'rgba(217, 119, 6, 0.1)',
  sending: 'rgba(217, 119, 6, 0.1)',
  undelivered: 'rgba(220, 38, 38, 0.1)',
  failed: 'rgba(220, 38, 38, 0.1)',
}

const STATUS_LABELS = {
  read: 'Read',
  delivered: 'Delivered',
  sent: 'Sent, not delivered yet',
  queued: 'Queued',
  accepted: 'Queued',
  sending: 'Sending',
  undelivered: 'Not delivered',
  failed: 'Failed',
}
// Statuses that need the practitioner's attention (the hint explains what went wrong).
const PROBLEM_STATUSES = ['undelivered', 'failed']

function timeAgo(value) {
  const time = value ? new Date(value).getTime() : NaN
  if (Number.isNaN(time)) return ''
  const seconds = Math.max(0, Math.round((Date.now() - time) / 1000))
  if (seconds < 60) return 'just now'
  const minutes = Math.round(seconds / 60)
  if (minutes < 60) return `${minutes}m ago`
  const hours = Math.round(minutes / 60)
  if (hours < 24) return `${hours}h ago`
  return `${Math.round(hours / 24)}d ago`
}

function absoluteTime(value) {
  const parsed = value ? dayjs(value) : null
  return parsed && parsed.isValid() ? parsed.format('D MMM, h:mm A') : ''
}

function ContactRow({ Icon, value, missingLabel }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px', minWidth: 0 }}>
      <Icon sx={{ fontSize: 15, color: value ? '#4b5563' : '#9ca3af', flexShrink: 0 }} />
      <Typography
        sx={{
          fontSize: '12px',
          color: value ? '#374151' : '#9ca3af',
          fontStyle: value ? 'normal' : 'italic',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {value || missingLabel}
      </Typography>
    </Box>
  )
}

// One send, structured: status chip + when (relative and absolute), the message itself, and —
// visibly, not behind a hover tooltip — the delivery hint when something needs attention.
function DeliveryCard({ delivery }) {
  const status = `${delivery?.status || ''}`.toLowerCase()
  const color = STATUS_COLORS[status] || '#6b7280'
  const bg = STATUS_BG[status] || 'rgba(107, 114, 128, 0.1)'
  const label = STATUS_LABELS[status] || delivery?.status || 'Unknown'
  const isProblem = PROBLEM_STATUSES.includes(status)
  const hint = delivery?.deliveryHint

  return (
    <Box
      sx={{
        border: '1px solid #e5e7eb',
        borderRadius: '12px',
        padding: '10px 12px',
        background: '#ffffff',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
        <Box
          component="span"
          sx={{
            fontSize: '11px',
            fontWeight: 700,
            color,
            backgroundColor: bg,
            padding: '2px 8px',
            borderRadius: '999px',
            whiteSpace: 'nowrap',
          }}
        >
          {label}
        </Box>
        <Typography sx={{ fontSize: '11.5px', color: '#6b7280', whiteSpace: 'nowrap' }}>
          {timeAgo(delivery?.sentAt)}
        </Typography>
        <Typography sx={{ fontSize: '11.5px', color: '#9ca3af', whiteSpace: 'nowrap', ml: 'auto' }}>
          {absoluteTime(delivery?.sentAt)}
        </Typography>
      </Box>
      {delivery?.preview && (
        <Typography
          sx={{
            fontSize: '12.5px',
            color: '#374151',
            lineHeight: 1.45,
            mt: '6px',
            display: '-webkit-box',
            WebkitLineClamp: 3,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
          }}
        >
          {delivery.preview}
        </Typography>
      )}
      {hint && (
        <Typography
          sx={{
            fontSize: '11.5px',
            color: isProblem ? '#b91c1c' : '#92400e',
            lineHeight: 1.4,
            mt: '6px',
            padding: '5px 8px',
            borderRadius: '8px',
            background: isProblem ? 'rgba(220, 38, 38, 0.06)' : 'rgba(217, 119, 6, 0.07)',
          }}
        >
          {hint}
        </Typography>
      )}
    </Box>
  )
}

/**
 * Compact "where do these sends actually go, and did they arrive?" block rendered under the
 * Patient Quick Actions buttons (mentor sources only).
 *
 * - Contact summary: the patient's WhatsApp number + email, partially masked server-side; the
 *   clinic's contact-visibility policy is respected (hidden -> a single "hidden" note).
 * - Recent sends: a one-line trigger showing the LATEST delivery state; the full list opens in a
 *   drawer (bottom sheet on phones, side panel on desktop) with live Twilio/Meta status per
 *   message and a human reason — a successful action only means "handed to WhatsApp", and the
 *   drawer is how a practitioner finds out about silent drops (Meta frequency caps, unreachable
 *   phone, non-WhatsApp number).
 *
 * @param {object}  contactContext same contact-context the quick actions use
 * @param {object}  caps           normalized capabilities from loadQuickActionCapabilities (or null while loading)
 * @param {boolean} showContact    render the masked contact rows
 * @param {boolean} showDeliveries render the recent-sends trigger + drawer
 * @param {number}  refreshKey     bump to force a delivery refetch (e.g. right after a send)
 */
export default function PatientContactDeliveryPanel({
  contactContext,
  caps,
  showContact = true,
  showDeliveries = true,
  refreshKey = 0,
}) {
  const [summary, setSummary] = useState(null)
  const [loading, setLoading] = useState(false)
  const [drawerOpen, setDrawerOpen] = useState(false)
  const theme = useTheme()
  const isPhone = useMediaQuery(theme.breakpoints.down('sm'))

  const canQuery = !!(contactContext?.bookedEventId || contactContext?.patientId)

  const refresh = useCallback(async () => {
    if (!canQuery) return
    setLoading(true)
    const result = await loadQuickActionDeliverySummary(contactContext, { sinceHours: 48, limit: 5 })
    setLoading(false)
    setSummary(result)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canQuery, contactContext?.bookedEventId, contactContext?.patientId, contactContext?.clinicBrandId, contactContext?.clinicId])

  useEffect(() => {
    if (!showDeliveries || !canQuery) return undefined
    // A fresh send takes a few seconds to get a terminal status from Meta — delay the
    // post-send refetch a little so the first answer is already meaningful.
    const timer = setTimeout(refresh, refreshKey > 0 ? 4000 : 0)
    return () => clearTimeout(timer)
  }, [showDeliveries, canQuery, refresh, refreshKey])

  if (!caps?.ok && !showDeliveries) {
    return null
  }

  const deliveries = summary?.ok ? summary.deliveries : []
  const latest = deliveries[0]
  const latestStatus = `${latest?.status || ''}`.toLowerCase()
  const problemCount = deliveries.filter((d) =>
    PROBLEM_STATUSES.includes(`${d?.status || ''}`.toLowerCase()),
  ).length

  // One-line summary for the collapsed trigger.
  let triggerSummary = null
  let triggerColor = '#6b7280'
  if (loading && !summary) {
    triggerSummary = 'Checking…'
  } else if (summary && (!summary.ok || summary.probeFailed)) {
    triggerSummary = 'Status unavailable'
  } else if (summary?.ok && !summary.whatsappAvailable) {
    triggerSummary = 'No WhatsApp on file'
  } else if (summary?.ok && deliveries.length === 0) {
    triggerSummary = 'None in 48h'
  } else if (latest) {
    triggerSummary = `${STATUS_LABELS[latestStatus] || latest.status} · ${timeAgo(latest.sentAt)}`
    triggerColor = STATUS_COLORS[latestStatus] || '#6b7280'
    if (problemCount > 0) {
      triggerSummary = `${problemCount} not delivered`
      triggerColor = '#dc2626'
    }
  }

  return (
    <Box sx={{ mt: '10px', pt: '10px', borderTop: '1px dashed #e5e7eb' }}>
      {showContact && caps?.ok && caps?.contactSummarySupported && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px', mb: showDeliveries ? '8px' : 0 }}>
          {caps.contactDetailsVisible ? (
            <>
              <ContactRow
                Icon={WhatsAppIcon}
                value={caps.whatsappNumberMasked}
                missingLabel="No WhatsApp number on file"
              />
              <ContactRow
                Icon={AlternateEmailOutlinedIcon}
                value={caps.emailMasked}
                missingLabel="No email on file"
              />
            </>
          ) : (
            <Typography sx={{ fontSize: '12px', color: '#9ca3af', fontStyle: 'italic' }}>
              Patient contact details are hidden for this clinic
            </Typography>
          )}
        </Box>
      )}

      {showDeliveries && (
        <>
          {/* Collapsed trigger: latest state at a glance, tap for the full story */}
          <Box
            component="button"
            type="button"
            onClick={() => setDrawerOpen(true)}
            aria-label="Open recent sends"
            sx={{
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '9px 10px',
              border: '1px solid #e5e7eb',
              borderRadius: '10px',
              background: '#fafafa',
              cursor: 'pointer',
              textAlign: 'left',
              '&:hover': { background: '#f3f4f6', borderColor: '#d1d5db' },
            }}
          >
            <ForumOutlinedIcon sx={{ fontSize: 16, color: '#6b7280', flexShrink: 0 }} />
            <Typography sx={{ fontSize: '12.5px', fontWeight: 600, color: '#374151', whiteSpace: 'nowrap' }}>
              Recent sends
            </Typography>
            <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: '5px', minWidth: 0 }}>
              {triggerSummary && (
                <>
                  <Box
                    sx={{
                      width: 7,
                      height: 7,
                      borderRadius: '50%',
                      backgroundColor: triggerColor,
                      flexShrink: 0,
                    }}
                  />
                  <Typography
                    sx={{
                      fontSize: '11.5px',
                      fontWeight: 600,
                      color: triggerColor,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {triggerSummary}
                  </Typography>
                </>
              )}
              <ChevronRightRoundedIcon sx={{ fontSize: 18, color: '#9ca3af', flexShrink: 0 }} />
            </Box>
          </Box>

          {/* Full list: bottom sheet on phones, side panel on desktop */}
          <Drawer
            anchor={isPhone ? 'bottom' : 'right'}
            open={drawerOpen}
            onClose={() => setDrawerOpen(false)}
            PaperProps={{
              sx: isPhone
                ? {
                    borderTopLeftRadius: '18px',
                    borderTopRightRadius: '18px',
                    maxHeight: '80dvh',
                    display: 'flex',
                    flexDirection: 'column',
                  }
                : { width: 420, maxWidth: '100vw', display: 'flex', flexDirection: 'column' },
            }}
          >
            <Box
              sx={{
                flexShrink: 0,
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                padding: '14px 16px',
                borderBottom: '1px solid #e5e7eb',
              }}
            >
              <WhatsAppIcon sx={{ fontSize: 20, color: '#059669', flexShrink: 0 }} />
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Typography sx={{ fontSize: '15px', fontWeight: 700, color: '#111827', lineHeight: 1.2 }}>
                  Recent sends
                </Typography>
                <Typography sx={{ fontSize: '11.5px', color: '#6b7280', mt: '1px' }}>
                  WhatsApp
                  {caps?.whatsappNumberMasked ? ` to ${caps.whatsappNumberMasked}` : ''} · last 48
                  hours
                </Typography>
              </Box>
              <IconButton size="small" onClick={refresh} disabled={loading} aria-label="Refresh delivery status">
                {loading ? (
                  <CircularProgress sx={{ height: '16px !important', width: '16px !important' }} />
                ) : (
                  <RefreshRoundedIcon sx={{ fontSize: 19, color: '#6b7280' }} />
                )}
              </IconButton>
              <IconButton size="small" onClick={() => setDrawerOpen(false)} aria-label="Close">
                <CloseIcon sx={{ fontSize: 19, color: '#6b7280' }} />
              </IconButton>
            </Box>

            <Box
              sx={{
                flex: 1,
                minHeight: 0,
                overflowY: 'auto',
                padding: '14px 16px',
                display: 'flex',
                flexDirection: 'column',
                gap: '10px',
                background: '#f9fafb',
              }}
            >
              {summary && !summary.ok && (
                <Typography sx={{ fontSize: '12.5px', color: '#9ca3af', textAlign: 'center', py: 2 }}>
                  Couldn&apos;t check delivery status.
                </Typography>
              )}
              {summary?.ok && summary.probeFailed && (
                <Typography sx={{ fontSize: '12.5px', color: '#9ca3af', textAlign: 'center', py: 2 }}>
                  Couldn&apos;t reach the messaging provider — try refreshing.
                </Typography>
              )}
              {summary?.ok && !summary.probeFailed && !summary.whatsappAvailable && (
                <Typography sx={{ fontSize: '12.5px', color: '#9ca3af', textAlign: 'center', py: 2 }}>
                  No WhatsApp number on file — nothing to track.
                </Typography>
              )}
              {summary?.ok && summary.whatsappAvailable && deliveries.length === 0 && (
                <Box sx={{ textAlign: 'center', py: 3 }}>
                  <ForumOutlinedIcon sx={{ fontSize: 30, color: '#d1d5db' }} />
                  <Typography sx={{ fontSize: '12.5px', color: '#9ca3af', mt: '6px' }}>
                    No WhatsApp sends to this patient in the last 48 hours.
                  </Typography>
                </Box>
              )}
              {deliveries.map((delivery) => (
                <DeliveryCard
                  key={delivery.messageSid || `${delivery.sentAt}-${delivery.status}`}
                  delivery={delivery}
                />
              ))}
            </Box>
          </Drawer>
        </>
      )}
    </Box>
  )
}
